import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Paper,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  Tooltip,
  Divider,
  Pagination,
  Stack
} from '@mui/material';
import { Search, Visibility, Edit, Delete, SwapHoriz } from '@mui/icons-material';
import useApi from 'hooks/useApi';
import ApiContext from 'contexts/ApiContext';
import ResourceTypeChanger from './ResourceTypeChanger';
import { renderSelectSearch } from 'components/SelectSearchField';
import { PERMISSIONS } from 'contexts/PermissionContext';
import { PermissionWrapper } from 'components/PermissionComponents';
import {
  LicenseVerificationPanel,
  LicenseVerificationEmpty,
  isLicenseValue
} from 'components/LicenseVerification';
import { ConsentBadges } from 'components/legal/ConsentStatus';
import ResourceConsentSection from 'components/legal/ResourceConsentSection';
import { ResourceConsentsSummary } from 'types/legal';

interface Field {
  name: string;
  type: 'text' | 'number' | 'email' | 'date' | 'textarea' | 'dropdown' | 'image' | 'license_verification';
  required: boolean;
  dropdownSource?: 'teams' | 'countryCode';
  dropdownOptions?: string[];
}

interface ResourceType {
  id: string;
  name: string;
  description: string;
  fields: Field[];
  createdAt: string;
  clientUID?: string;
  category?: 'user' | 'others';
  access?: string[];
  invite?: boolean;
  allowRating?: boolean;
}

interface ResourceData {
  id: string;
  resourceTypeId: string | { id: string; name: string; [key: string]: any };
  data: { [key: string]: any };
  // Current consent state, carried on every resource response (absent = nothing on record).
  consents?: ResourceConsentsSummary | null;
  // Client that owns this resource's type, resolved server-side. Needed because
  // the Super Admin "All" listing mixes rows from different clients.
  client?: { uid: string; name: string } | null;
  createdAt: string;
  updatedAt: string;
}

interface UniversalResourceSearchProps {
  clientUID: string | null;
}

const UniversalResourceSearch: React.FC<UniversalResourceSearchProps> = ({ clientUID }) => {
  const { getResourceTypes, getAllResourceData, showToast, deleteResourceData, updateResourceData, getTeamByClient } = useApi();
  const apiCtx = useContext(ApiContext);
  const uploadAsset = apiCtx?.uploadAsset;
  const [resourceTypes, setResourceTypes] = useState<ResourceType[]>([]);
  const [selectedType, setSelectedType] = useState<string>('all');
  const [allResourceData, setAllResourceData] = useState<ResourceData[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [viewDialog, setViewDialog] = useState<{ open: boolean; data: ResourceData | null }>({ open: false, data: null });
  const [editDialog, setEditDialog] = useState<{ open: boolean; data: ResourceData | null }>({ open: false, data: null });
  const [editFormData, setEditFormData] = useState<{ [key: string]: any }>({});
  const [editLoading, setEditLoading] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  
  // Selection state for bulk operations
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [resourceTypeChangerOpen, setResourceTypeChangerOpen] = useState(false);

  // Dropdown data for edit mode
  const [teams, setTeams] = useState<any[]>([]);
  const [editDialogTeams, setEditDialogTeams] = useState<any[]>([]);
  const [teamSearchText, setTeamSearchText] = useState('');
  const [resourceTypeSearchText, setResourceTypeSearchText] = useState('');

  const filteredResourceTypes = (Array.isArray(resourceTypes) ? resourceTypes : []).filter(
    (resourceType: any) =>
      (resourceType.name || '').toLowerCase().includes(resourceTypeSearchText.toLowerCase())
  );
  const [loadingDropdownData, setLoadingDropdownData] = useState(false);
  const [editFormLoading, setEditFormLoading] = useState(false);
  const [formRefreshKey, setFormRefreshKey] = useState(0);
  const searchControllerRef = useRef<AbortController | null>(null);

  // Country codes for dropdown
  const countryCodes = [
    '1', '7', '20', '27', '30', '31', '32', '33', '34', '36', '39', '40', '41', '43', '44', '45', '46', '47', '48', '49', '51', '52', '53', '54', '55', '56', '57', '58', '60', '61', '62', '63', '64', '65', '66', '81', '82', '84', '86', '87', '087', '90', '91', '92', '93', '94', '95', '98', '212', '213', '216', '218', '220', '221', '222', '223', '224', '225', '226', '227', '228', '229', '230', '231', '232', '233', '234', '235', '236', '237', '238', '239', '240', '241', '242', '243', '244', '245', '246', '248', '249', '250', '251', '252', '253', '254', '255', '256', '257', '258', '260', '261', '262', '263', '264', '265', '266', '267', '268', '269', '290', '291', '297', '298', '299', '350', '351', '352', '353', '354', '355', '356', '357', '358', '359', '370', '371', '372', '373', '374', '375', '376', '377', '378', '380', '381', '382', '383', '385', '386', '387', '389', '420', '421', '423', '500', '501', '502', '503', '504', '505', '506', '507', '508', '509', '590', '591', '592', '593', '594', '595', '596', '597', '598', '599', '670', '672', '673', '674', '675', '676', '677', '678', '679', '680', '681', '682', '683', '684', '685', '686', '687', '688', '689', '690', '691', '692', '850', '852', '853', '855', '856', '880', '886', '960', '961', '962', '963', '964', '965', '966', '967', '968', '970', '971', '972', '973', '974', '975', '976', '977', '992', '993', '994', '995', '996', '998'
  ];

  // Phone numbers are typed with punctuation that is not stored (+1 (555) 123-4567),
  // so that punctuation is stripped before the term goes to the server. An email is
  // stored verbatim and its dots are significant — stripping them turned
  // "ajay@quikstep.co" into "ajay@quikstepco", which matches nothing. Leave an
  // email-shaped term alone.
  const isEmailTerm = (term: string) => term.includes('@');
  const sanitizeSearchTerm = (term: string) =>
    isEmailTerm(term) ? term.trim() : term.replace(/[\s\+\-\(\)\.]/g, '');

  useEffect(() => {
    loadResourceTypes();
  }, [clientUID]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    loadResourceData();
  }, [clientUID, selectedType, currentPage, pageSize, debouncedSearchTerm]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
    setSelectedItems(new Set());
    setSelectAll(false);
  }, [selectedType, debouncedSearchTerm]);

  const loadResourceTypes = async () => {
    try {
      const response = await getResourceTypes(clientUID || undefined, null);
      if (Array.isArray(response)) {
        setResourceTypes(response);
      } else if (response?.data && Array.isArray(response.data)) {
        setResourceTypes(response.data);
      } else {
        setResourceTypes([]);
        showToast('Failed to load resource types', 'error');
      }
    } catch (error) {
      setResourceTypes([]);
      showToast('Failed to load resource types', 'error');
    }
  };

  const loadAllResourceData = async () => {
    // Abort any previous in-flight request before starting a new one
    searchControllerRef.current?.abort();
    const controller = new AbortController();
    searchControllerRef.current = controller;

    setLoading(true);
    try {
      const searchWords = debouncedSearchTerm.trim().split(/\s+/).filter(Boolean);
      const isMultiWord = searchWords.length > 1;

      const sanitizedWords = searchWords.map(sanitizeSearchTerm);
      const params: any = {
        page: currentPage,
        limit: pageSize,
        search: isMultiWord ? sanitizedWords[0] : (sanitizeSearchTerm(debouncedSearchTerm.trim()) || undefined),
        clientUID: clientUID || undefined
      };

      // Only add resourceTypeId if it's not 'all'
      if (selectedType !== 'all') {
        params.resourceTypeId = selectedType;
      }

      const response = await getAllResourceData(params, { signal: controller.signal });

      // Discard result if this request was superseded
      if (controller.signal.aborted) return;

      if (response?.success) {
        // Handle paginated response with nested data structure
        const resourceData = response.data?.data || [];
        const pagination = response.data?.pagination || {};

        // Keep the original data structure without transforming resourceTypeId
        // This preserves the full resource type object for display purposes
        setAllResourceData(resourceData);
        setTotalPages(pagination.totalPages || 0);
        setTotalItems(pagination.totalCount || 0);
      } else if (Array.isArray(response)) {
        // Handle direct array response (fallback)
        setAllResourceData(response);
        setTotalPages(1);
        setTotalItems(response.length);
      } else if (response?.data && Array.isArray(response.data)) {
        // Handle wrapped array response
        setAllResourceData(response.data);
        setTotalPages(response.totalPages || 1);
        setTotalItems(response.totalItems || response.data.length);
      } else {
        setAllResourceData([]);
        setTotalPages(0);
        setTotalItems(0);
      }
    } catch (error: any) {
      // Silently discard aborted/cancelled requests — do not update state or show an error toast
      if (controller.signal.aborted) return;
      console.error('Failed to load resource data:', error);
      setAllResourceData([]);
      setTotalPages(0);
      setTotalItems(0);
      showToast('Failed to load resource data', 'error');
    } finally {
      // Only clear loading if this is still the active request
      if (searchControllerRef.current === controller) {
        setLoading(false);
      }
    }
  };

  // Alias for backward compatibility
  const loadResourceData = loadAllResourceData;

  // Consent applies to people only — user-category resource types.
  const isUserResource = (item: ResourceData | null) => {
    if (!item) return false;
    if (typeof item.resourceTypeId === 'object') return item.resourceTypeId?.category === 'user';
    return resourceTypes.find((rt) => rt.id === item.resourceTypeId)?.category === 'user';
  };
  const hasPhone = (item: ResourceData) => Boolean(String(item.data?.phoneNumber ?? '').trim());

  const handleTypeChange = (event: SelectChangeEvent<string>) => {
    setSelectedType(event.target.value);
  };

  // Client-side multi-word search (e.g. "John Ball" across firstName + lastName fields)
  const filteredResults = useMemo(() => {
    if (!debouncedSearchTerm.trim()) return allResourceData;

    const words = debouncedSearchTerm.trim().toLowerCase().split(/\s+/);
    if (words.length <= 1) return allResourceData; // single word handled server-side

    return allResourceData.filter(item => {
      const combinedRaw = Object.values(item.data)
        .map(v => (Array.isArray(v) ? v.join(' ') : String(v ?? '')))
        .join(' ')
        .toLowerCase();
      // Stored values sanitized the same way as the term, so a typed phone number
      // still matches a differently punctuated stored one.
      const combinedSanitized = combinedRaw.replace(/[\s\+\-\(\)\.]/g, '');

      // An email word keeps its dots, so it has to be tested against the raw
      // values — against the sanitized ones it could never match.
      return words.every(word =>
        isEmailTerm(word)
          ? combinedRaw.includes(word)
          : combinedSanitized.includes(sanitizeSearchTerm(word))
      );
    });
  }, [allResourceData, debouncedSearchTerm]);

  // Pagination handlers
  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
    setPageSize(event.target.value as number);
    setCurrentPage(1); // Reset to first page when page size changes
  };

  // Selection handlers
  const handleSelectItem = (itemId: string, checked: boolean) => {
    const newSelection = new Set(selectedItems);
    if (checked) {
      newSelection.add(itemId);
    } else {
      newSelection.delete(itemId);
    }
    setSelectedItems(newSelection);
    
    // Update select all state
    if (newSelection.size === filteredResults.length && filteredResults.length > 0) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set(filteredResults.map(item => item.id));
      setSelectedItems(allIds);
      setSelectAll(true);
    } else {
      setSelectedItems(new Set());
      setSelectAll(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) return;
    
    if (window.confirm(`Are you sure you want to delete ${selectedItems.size} selected items?`)) {
      try {
        const deletePromises = Array.from(selectedItems).map(id => deleteResourceData(id));
        await Promise.all(deletePromises);
        showToast(`Successfully deleted ${selectedItems.size} items`, 'success');
        setSelectedItems(new Set());
        setSelectAll(false);
        loadResourceData();
      } catch (error) {
        showToast('Failed to delete some items', 'error');
      }
    }
  };

  const handleBulkChangeResourceType = () => {
    if (selectedItems.size === 0) {
      showToast('Please select items to change resource type', 'warning');
      return;
    }
    setResourceTypeChangerOpen(true);
  };

  const handleResourceTypeChangeComplete = () => {
    setResourceTypeChangerOpen(false);
    setSelectedItems(new Set());
    setSelectAll(false);
    loadResourceData();
  };

  // Action handlers
  const fetchSignedUrlForImage = async (imageData: any): Promise<string | null> => {
    if (!imageData?.file_path || !apiCtx) return null;
    try {
      const res = await apiCtx.getSignedUrl({ file_path: imageData.file_path });
      return res?.data?.signed_url || res?.data?.signedUrl || res?.data?.url || null;
    } catch { return null; }
  };

  const enrichWithSignedUrls = async (data: any, item: any) => {
    const fields = typeof item.resourceTypeId === 'object' ? item.resourceTypeId.fields : [];
    for (const field of fields) {
      const val = data[field.name];
      if (val && typeof val === 'object' && !Array.isArray(val) && val.file_path && !val.signedUrl) {
        const signedUrl = await fetchSignedUrlForImage(val);
        if (signedUrl) data[field.name] = { ...val, signedUrl };
      }
    }
    return data;
  };

  const handleView = async (item: ResourceData) => {
    const enrichedItem = { ...item, data: { ...item.data } };
    await enrichWithSignedUrls(enrichedItem.data, item);
    setViewDialog({ open: true, data: enrichedItem });

    if (teams.length === 0 && editDialogTeams.length === 0) {
      const resourceTypeId = typeof item.resourceTypeId === 'object' ? item.resourceTypeId.id : item.resourceTypeId;
      await loadDropdownData(resourceTypeId, item.client?.uid);
    }
  };
  // The teams endpoint returns creation order, which reads as unordered once a
  // client has more than a handful. Sorted here rather than at the render sites so
  // every consumer of `teams`/`editDialogTeams` gets the same order. Entries come
  // through as objects or bare strings depending on the response shape.
  const teamSortLabel = (team: any): string =>
    (typeof team === 'string' ? team : team?.name || team?.title || '').trim();

  const sortTeamsByName = (list: any[]): any[] =>
    [...list].sort((a, b) => teamSortLabel(a).localeCompare(teamSortLabel(b), undefined, { sensitivity: 'base' }));

  // Turn a raw field key into a human-readable label, e.g. "firstName" ->
  // "First Name", "local_language_name" -> "Local Language Name". The field key
  // itself (field.name) is still used for data access; this is display only.
  const humanizeLabel = (name: string): string => {
    if (!name) return '';
    return name
      .replace(/[_-]+/g, ' ')
      .replace(/([a-z\d])([A-Z])/g, '$1 $2')
      .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  // Resolve a stored team value (a single UID or an array/comma-separated list
  // of UIDs) to the team name(s) for display, mirroring the edit dialog's
  // renderValue. Without this the view dialog shows the raw team UIDs.
  const resolveTeamNames = (value: any): string => {
    const teamsToUse = editDialogTeams.length > 0 ? editDialogTeams : teams;
    const uids = Array.isArray(value)
      ? value
      : String(value).split(',').map((v) => v.trim()).filter(Boolean);
    return uids
      .map((uid) => {
        const team = teamsToUse.find((t) =>
          typeof t === 'object' ? (t.uid || t.id) === uid : t === uid
        );
        return team && typeof team === 'object' ? (team.name || team.title || uid) : (team || uid);
      })
      .join(', ');
  };

  // Load dropdown data for edit mode.
  // `rowClientUID` is the owning client carried on the row itself — needed in the
  // Super Admin "All" view, where there is no page-level client. It cannot be
  // looked up from `resourceTypes` instead: that list is keyed by resource-type
  // uid, while `item.resourceTypeId` is the populated document whose `id` is the
  // Mongo _id, so the two never match.
  const loadDropdownData = async (resourceTypeId: string, rowClientUID?: string | null) => {
    let loadedTeams: any[] = [];

    if (resourceTypeId) {
      const ownerClientUID = clientUID || rowClientUID || null;

      if (!ownerClientUID) {
        console.warn('No client could be resolved for resource type, teams not loaded:', resourceTypeId);
        setTeams([]);
        setEditDialogTeams([]);
        return { teams: [] };
      }

      setLoadingDropdownData(true);
      try {
        const response = await getTeamByClient(ownerClientUID);
        
        if (response?.success && Array.isArray(response?.data)) {
          loadedTeams = sortTeamsByName(response.data);
          setTeams(loadedTeams);
          setEditDialogTeams(loadedTeams);
          setFormRefreshKey(prev => prev + 1); // Force form refresh
        } else if (Array.isArray(response)) {
          // Handle case where response is directly an array
          loadedTeams = sortTeamsByName(response);
          setTeams(loadedTeams);
          setEditDialogTeams(loadedTeams);
          setFormRefreshKey(prev => prev + 1); // Force form refresh
        } else {
          console.warn('Failed to load teams, unexpected response format:', response);
          setTeams([]);
          setEditDialogTeams([]);
          loadedTeams = [];
        }
      } catch (error) {
        setTeams([]);
        setEditDialogTeams([]);
        loadedTeams = [];
        showToast('Failed to load teams for dropdown', 'error');
      } finally {
        setLoadingDropdownData(false);
      }
    }

    return { teams: loadedTeams };
  };

  const handleEdit = async (item: ResourceData) => {
    setEditDialog({ open: true, data: item });
    setTeamSearchText('');
    setEditFormLoading(true);

    try {
      // Combine data from nested data object and flattened root level fields
      let combinedData = { ...item.data };

      // Fetch signed URLs for image fields
      await enrichWithSignedUrls(combinedData, item);
      const resourceTypeId = typeof item.resourceTypeId === 'object' ? item.resourceTypeId.id : item.resourceTypeId;
      const resourceType: any = item.resourceTypeId;
      const fields = typeof item.resourceTypeId === 'object' ? item.resourceTypeId.fields : undefined;
      
      // Add flattened fields to form data
      fields?.forEach((field: Field) => {
        if (item[field.name as keyof ResourceData] !== undefined) {
          combinedData[field.name] = item[field.name as keyof ResourceData];
        }
      });
      
      // Load dropdown data first, then patch form values
      const { teams: loadedTeams } = await loadDropdownData(resourceTypeId, item.client?.uid);
    
    // Process form data after dropdown data is loaded - especially for teams
    if (resourceType) {
      // Process dropdown fields with proper value mapping
      resourceType.fields.forEach((field:any) => {
        if (field.type === 'dropdown' && field.dropdownSource === 'teams' && field.name === 'team') {
          const teamValue = combinedData[field.name];
          
          if (teamValue) {
            // Since dropdown now uses UIDs as values, we need to ensure the value is a UID
            const teamsToUse = loadedTeams.length > 0 ? loadedTeams : teams;
            if (teamsToUse.length > 0) {
              let resolvedValue = teamValue;
              
              // Handle array of team values
              if (Array.isArray(teamValue)) {
                resolvedValue = teamValue.map(singleTeamValue => {
                  if (typeof teamsToUse[0] === 'object') {
                    // Teams are objects - check if value is UID or name
                    const teamByUid = teamsToUse.find(t => (t.uid || t.id) === singleTeamValue);
                    const teamByName = teamsToUse.find(t => (t.name || t.title) === singleTeamValue);
                    
                      if (teamByUid) return singleTeamValue;
                      if (teamByName) return teamByName.uid || teamByName.id;
                      return singleTeamValue;
                    } else if (typeof teamsToUse[0] === 'string') {
                      return singleTeamValue;
                    }
                    return singleTeamValue;
                  });
                } else {
                // Handle single team value
                if (typeof teamsToUse[0] === 'object') {
                  // Teams are objects - check if value is UID or name
                  const teamByUid = teamsToUse.find(t => (t.uid || t.id) === teamValue);
                  const teamByName = teamsToUse.find(t => (t.name || t.title) === teamValue);
                  
                  if (teamByUid) {
                    // Value is already a UID, keep it as is
                    resolvedValue = teamValue;
                    } else if (teamByName) {
                    // Value is a name, convert to UID
                      resolvedValue = teamByName.uid || teamByName.id;
                    }
                  }
                }
              combinedData[field.name] = resolvedValue;
              }
            }
          }
        });
      }
    
    setEditFormData(combinedData);
    
    } catch (error) {
      console.error('Error in handleEdit:', error);
      showToast('Failed to load edit form data', 'error');
    } finally {
      setEditFormLoading(false); // Stop loading
    }
  };
  const handleDelete = async (item: ResourceData) => {
    if (window.confirm('Are you sure you want to delete this resource data?')) {
      try {      await deleteResourceData(item.id);
      showToast('Resource data deleted successfully', 'success');
      loadResourceData();
      } catch (error) {
        showToast('Failed to delete resource data', 'error');
      }
    }
  };

  const handleEditFieldChange = (fieldName: string, value: any) => {
    console.log(`Field change: ${fieldName} = ${value} (type: ${typeof value})`);
    setEditFormData(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleEditSave = async () => {
    console.log('=== SAVE BUTTON CLICKED ===');
    
    if (!editDialog.data) {
      console.error('No edit dialog data available');
      return;
    }
    
    const resourceType: any = editDialog?.data?.resourceTypeId;
    
    // Validate required fields
    const missingFields = resourceType.fields.filter((field: any) => {
      if (!field.required) return false;
      const val = editFormData[field.name];
      if (val instanceof File) return false;
      if (val && typeof val === 'object' && (val.file_path || val.public_url)) return false;
      if (!val) return true;
      return !val?.toString().trim();
    }).map((field:any) => field.name);
    if (missingFields.length > 0) {
      console.error('Missing required fields:', missingFields);
      showToast(`Please fill in required fields: ${missingFields.join(', ')}`, 'error');
      return;
    }
    
    setEditLoading(true);
    try {
      // Upload image files first, then include URLs in the payload
      let processedFormData: { [key: string]: any } = {};
      // Licence fields are owned by the verification pipeline — echoing them back
      // could clobber a value Jumio's callback enriched while the dialog was open.
      const licenseFieldNames = new Set(
        ((resourceType?.fields as any[]) || [])
          .filter((f: any) => f?.type === 'license_verification')
          .map((f: any) => f.name)
      );
      for (const [key, val] of Object.entries(editFormData)) {
        if (licenseFieldNames.has(key) || isLicenseValue(val)) {
          continue;
        }
        if (val instanceof File) {
          try {
            const res = await uploadAsset!(val, `resource-images/${editDialog.data.id}`);
            const imgData = res?.data;
            if (imgData) {
              processedFormData[key] = {
                file_name: imgData.file_name,
                file_path: imgData.file_path,
                mime_type: imgData.mime_type,
                public_url: imgData.public_url,
              };
            }
          } catch (imgErr) {
            console.error(`Failed to upload image for ${key}:`, imgErr);
            showToast(`Failed to upload image for ${key}`, 'error');
            setEditLoading(false);
            return;
          }
        } else if (val !== null && val !== undefined) {
          processedFormData[key] = val;
        }
      }

      const basePayload = {
        resourceTypeId: resourceType.id,
        data: processedFormData
      };
      
      console.log('Base payload:', basePayload);

      console.log('=== CALLING UPDATE API ===');
      if (resourceType.category === 'user') {
        // For user resource types, include category and flatten user fields to root level
        const additionalPayload = {
          category: 'user',
          // Flatten user fields to root level
          ...processedFormData
        };
        
        await updateResourceData(editDialog.data.id, basePayload, additionalPayload);
      } else {
        await updateResourceData(editDialog.data.id, basePayload);
      }

      console.log('=== API CALL SUCCESSFUL ===');
      showToast('Resource data updated successfully', 'success');
      setEditDialog({ open: false, data: null });
      setEditFormData({});
      setEditDialogTeams([]);
      setEditFormLoading(false);
      setFormRefreshKey(0);
      loadResourceData();
    } catch (error: any) {
      console.error('Update API error:', error);
      showToast(error?.message, 'error');
    } finally {
      setEditLoading(false);
    }
  };

  const handleEditCancel = () => {
    setEditDialog({ open: false, data: null });
    setEditFormData({});
    setEditDialogTeams([]);
    setEditFormLoading(false);
    setFormRefreshKey(0);
  };

  const handleViewClose = () => {
    setViewDialog({ open: false, data: null });
  };

  const renderEditField = (field: Field) => {
    const value = editFormData[field.name] || '';
    const rawVal = editFormData[field.name];
    const label = humanizeLabel(field.name);

    // Read-only — a licence is written by the mobile Jumio scan, never edited here.
    // Checked before the image branch: a licence value carries nested front/back
    // images and would otherwise be mistaken for a plain image object.
    if (field.type === 'license_verification' || isLicenseValue(rawVal)) {
      return (
        <Box key={field.name} sx={{ mb: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
            {label}
          </Typography>
          {isLicenseValue(rawVal) ? (
            <LicenseVerificationPanel value={rawVal} fieldName={field.name} />
          ) : (
            <LicenseVerificationEmpty fieldName={field.name} />
          )}
        </Box>
      );
    }

    const isImageObj = rawVal && typeof rawVal === 'object' && !Array.isArray(rawVal) && !(rawVal instanceof File) && (rawVal.file_path || rawVal.public_url);
    const isImageFile = rawVal instanceof File;

    if (field.type === 'image' || isImageObj || isImageFile) {
      const isFile = rawVal instanceof File;
      const imagePreview = isFile
        ? URL.createObjectURL(rawVal)
        : (typeof rawVal === 'object' && (rawVal?.signedUrl || rawVal?.public_url))
          ? (rawVal.signedUrl || rawVal.public_url)
          : null;

      return (
        <Box key={field.name} sx={{ mb: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {humanizeLabel(field.name)} {field.required && '*'}
          </Typography>
          {imagePreview && (
            <Box sx={{ mb: 1 }}>
              <Box component="img" src={imagePreview} alt={field.name}
                sx={{ maxWidth: 200, maxHeight: 150, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}
                onError={(e: any) => { e.target.src = ''; e.target.alt = 'Image failed to load'; e.target.style.border = '1px dashed #ccc'; e.target.style.padding = '20px'; }} />
            </Box>
          )}
          <Button variant="outlined" component="label" size="small">
            {imagePreview ? 'Replace Image' : 'Upload Image'}
            <input type="file" hidden accept="image/*"
              onChange={(e) => { const file = e.target.files?.[0]; if (file) handleEditFieldChange(field.name, file); }} />
          </Button>
          {imagePreview && !isFile && rawVal?.file_name && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>{rawVal.file_name}</Typography>
          )}
        </Box>
      );
    }

    switch (field.type) {
      case 'textarea':
        return (
          <TextField
            key={field.name}
            fullWidth
            label={label}
            value={value}
            onChange={e => handleEditFieldChange(field.name, e.target.value)}
            required={field.required}
            multiline
            rows={3}
            variant="outlined"
            size="small"
            sx={{ mb: 1 }}
          />
        );
      case 'number':
        return (
          <TextField
            key={field.name}
            fullWidth
            label={label}
            type="number"
            value={value}
            onChange={e => handleEditFieldChange(field.name, e.target.value)}
            required={field.required}
            variant="outlined"
            size="small"
            sx={{ mb: 1 }}
          />
        );
      case 'email':
        return (
          <TextField
            key={field.name}
            fullWidth
            label={label}
            type="email"
            value={value}
            onChange={e => handleEditFieldChange(field.name, e.target.value)}
            required={field.required}
            variant="outlined"
            size="small"
            sx={{ mb: 1 }}
          />
        );
      case 'date':
        return (
          <TextField
            key={field.name}
            fullWidth
            label={label}
            type="date"
            value={value}
            onChange={e => handleEditFieldChange(field.name, e.target.value)}
            required={field.required}
            variant="outlined"
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 1 }}
          />
        );
      case 'dropdown':
        
        // Render actual dropdown based on dropdownSource
        if (field.dropdownSource === 'teams') {
          const teamsToUse = editDialogTeams.length > 0 ? editDialogTeams : teams;
          const isMultiple = Array.isArray(value);

          const teamOptions = teamsToUse.map((team: any) =>
            team && typeof team === 'object'
              ? { value: team.uid || team.id, label: team.name || team.title || team.uid || team.id || 'Unknown Team' }
              : { value: String(team), label: String(team) }
          );

          // Clients with a long team list are unusable without a filter.
          const filteredTeamOptions = teamOptions.filter((opt: any) =>
            opt.label.toLowerCase().includes(teamSearchText.toLowerCase())
          );

          const labelFor = (val: string) =>
            teamOptions.find((opt: any) => opt.value === val)?.label || val;

          return (
            <FormControl
              key={field.name}
              fullWidth
              variant="outlined"
              size="small"
              required={field.required}
              sx={{ mb: 1 }}
            >
              <InputLabel>{label}</InputLabel>
              <Select
                multiple={isMultiple}
                value={isMultiple ? (value || []) : (value || '')}
                onChange={e => handleEditFieldChange(field.name, e.target.value)}
                label={label}
                disabled={loadingDropdownData}
                MenuProps={{
                  disableAutoFocusItem: true,
                  PaperProps: { style: { maxHeight: 300 } }
                }}
                renderValue={isMultiple ? (selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).map((val) => (
                      <Chip key={val} label={labelFor(val)} size="small" />
                    ))}
                  </Box>
                ) : (selected) => (selected ? labelFor(selected as string) : '')}
              >
                {renderSelectSearch(teamSearchText, setTeamSearchText, 'Search teams...')}

                {!field.required && !isMultiple && (
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                )}

                {loadingDropdownData ? (
                  <MenuItem disabled>
                    <em>Loading...</em>
                  </MenuItem>
                ) : filteredTeamOptions.length === 0 ? (
                  <MenuItem disabled>
                    <em>{teamSearchText ? 'No teams found matching search' : 'No teams available'}</em>
                  </MenuItem>
                ) : (
                  filteredTeamOptions.map((option: any) => (
                    <MenuItem key={option.value} value={option.value}>
                      {isMultiple && (
                        <Checkbox checked={(value || []).indexOf(option.value) > -1} />
                      )}
                      {option.label}
                    </MenuItem>
                  ))
                )}
              </Select>
              {loadingDropdownData && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  Loading team options...
                </Typography>
              )}
            </FormControl>
          );
        } else if (field.dropdownSource === 'countryCode') {
          return (
            <FormControl
              key={field.name}
              fullWidth
              variant="outlined"
              size="small"
              required={field.required}
              sx={{ mb: 1 }}
            >
              <InputLabel>{label}</InputLabel>
              <Select
                value={value || ''}
                onChange={e => handleEditFieldChange(field.name, e.target.value)}
                label={label}
              >
                {!field.required && (
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                )}
                {countryCodes.map((code) => (
                  <MenuItem key={code} value={code}>
                    {code}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        } else if (field.dropdownOptions && Array.isArray(field.dropdownOptions)) {
          // Handle custom dropdown options
          return (
            <FormControl
              key={field.name}
              fullWidth
              variant="outlined"
              size="small"
              required={field.required}
              sx={{ mb: 1 }}
            >
              <InputLabel>{label}</InputLabel>
              <Select
                value={value || ''}
                onChange={e => handleEditFieldChange(field.name, e.target.value)}
                label={label}
              >
                {!field.required && (
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                )}
                {field.dropdownOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        } else {
          // Fallback for dropdown fields without a recognized source
          return (
            <TextField
              key={field.name}
              fullWidth
              label={`${label} (Unknown dropdown source)`}
              value={value || ''}
              onChange={e => handleEditFieldChange(field.name, e.target.value)}
              required={field.required}
              variant="outlined"
              size="small"
              sx={{ mb: 1 }}
              helperText="Unknown dropdown source - enter value manually"
            />
          );
        }
      default:
        return (
          <TextField
            key={field.name}
            fullWidth
            label={label}
            value={typeof value === 'object' ? '' : (value || '')}
            onChange={e => handleEditFieldChange(field.name, e.target.value)}
            required={field.required}
            variant="outlined"
            size="small"
            sx={{ mb: 1 }}
          />
        );
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Filter and Search Controls */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="resource-type-select-label">Resource Type</InputLabel>
          <Select
            labelId="resource-type-select-label"
            value={selectedType}
            label="Resource Type"
            onChange={handleTypeChange}
            MenuProps={{
              disableAutoFocusItem: true,
              PaperProps: { style: { maxHeight: 300 } }
            }}
          >
            {renderSelectSearch(resourceTypeSearchText, setResourceTypeSearchText, 'Search resource types...')}

            <MenuItem value="all">
              <em>All Types</em>
            </MenuItem>
            {filteredResourceTypes.length === 0 && (
              <MenuItem disabled>
                <em>No resource types found matching search</em>
              </MenuItem>
            )}
            {filteredResourceTypes.map(type => (
              <MenuItem key={type.id} value={type.id}>{type.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          placeholder="Search any field, value, or resource type..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: loading && debouncedSearchTerm !== searchTerm ? (
              <InputAdornment position="end">
                <CircularProgress size={16} />
              </InputAdornment>
            ) : null,
          }}
          sx={{ minWidth: 260, flex: 1 }}
        />
      </Box>

      {/* Filter Summary */}
      {(selectedType !== 'all' || debouncedSearchTerm) && (
        <Box sx={{ mb: 2, p: 2, borderRadius: 1 }}>
          <Typography variant="body2" color="info.main">
            <strong>Active Filters:</strong>
            {selectedType !== 'all' && (
              <Chip 
                label={`Type: ${resourceTypes.find(rt => rt.id === selectedType)?.name || 'Unknown'}`} 
                size="small" 
                sx={{ ml: 1, mr: 1 }} 
                onDelete={() => setSelectedType('all')}
              />
            )}
            {debouncedSearchTerm && (
              <Chip 
                label={`Search: "${debouncedSearchTerm}"`} 
                size="small" 
                sx={{ ml: 1 }} 
                onDelete={() => setSearchTerm('')}
              />
            )}
            {totalItems > 0 && (
              <span style={{ marginLeft: '8px' }}>
                ({totalItems} result{totalItems !== 1 ? 's' : ''} found)
              </span>
            )}
          </Typography>
        </Box>
      )}

      {/* Bulk Actions Bar */}
      {filteredResults.length > 0 && (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2, 
          mb: 2, 
          p: 2, 
          bgcolor: 'grey.50', 
          borderRadius: 1,
          borderLeft: '4px solid',
          borderLeftColor: selectedItems.size > 0 ? 'primary.main' : 'grey.300'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Checkbox
              checked={selectAll}
              indeterminate={selectedItems.size > 0 && selectedItems.size < filteredResults.length}
              onChange={(e) => handleSelectAll(e.target.checked)}
              size="small"
            />
            <Typography variant="body2" color="text.secondary">
              {selectedItems.size > 0 
                ? `${selectedItems.size} of ${filteredResults.length} selected`
                : `Select all ${filteredResults.length} items`
              }
            </Typography>
          </Box>
          
          <Divider orientation="vertical" flexItem />
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <PermissionWrapper permission={PERMISSIONS.ADD_EDIT_RESOURCE} disableInsteadOfHide={true}>
            <Tooltip title="Change Resource Type">
              <IconButton
                size="small"
                color="primary"
                disabled={selectedItems.size === 0}
                onClick={handleBulkChangeResourceType}
              >
                <SwapHoriz />
              </IconButton>
            </Tooltip>
            </PermissionWrapper>
            <Tooltip title="Delete Selected">
              <IconButton
                size="small"
                color="error"
                disabled={selectedItems.size === 0}
                onClick={handleBulkDelete}
              >
                <Delete />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}

      <Paper sx={{ p: 0, minHeight: 200 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : filteredResults.length === 0 ? (
          <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 4 }}>
            No results found.
          </Typography>
        ) : (
          <Box>
            {filteredResults.map(item => {
              // Handle both object and string resourceTypeId
              const resourceTypeId = typeof item.resourceTypeId === 'object' ? item.resourceTypeId.id : item.resourceTypeId;
              const type = resourceTypes.find(rt => rt.id === resourceTypeId);
              // If resourceTypeId is an object, we can use its name directly
              const typeName = typeof item.resourceTypeId === 'object' ? item.resourceTypeId.name : (type?.name || 'Unknown');
              const isSelected = selectedItems.has(item.id);
              return (
                <Box
                  key={item.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    borderBottom: '1px solid #eee',
                    px: 2,
                    py: 1.5,
                    bgcolor: isSelected ? '#e3f2fd' : '#fafbfc',
                    borderRadius: 1,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                    mb: 1.5,
                    '&:last-child': { borderBottom: 'none', mb: 0 },
                    minHeight: 56,
                    transition: 'background 0.2s',
                    '&:hover': { bgcolor: isSelected ? '#e1f5fe' : '#f0f4ff' }
                  }}
                >
                  <Checkbox
                    checked={isSelected}
                    onChange={(e) => handleSelectItem(item.id, e.target.checked)}
                    size="small"
                    sx={{ mr: 1 }}
                  />
                  <Chip label={typeName} size="small" color="primary" sx={{ mr: 1 }} />
                  <Box sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: '5px 16px', alignItems: 'center' }}>
                    {Object.entries(item.data).slice(0, 3).map(([k, v]) => (
                      <Box key={k} sx={{ minWidth: 80, maxWidth: 180, overflow: 'hidden', mb: 0.5 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: 12, lineHeight: 1.2 }}>
                          {k}
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: 13, lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {v?.toString() || '-'}
                        </Typography>
                      </Box>
                    ))}
                    {Object.entries(item.data).length > 3 && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        +{Object.entries(item.data).length - 3} more
                      </Typography>
                    )}
                  </Box>
                  {/* Which client the resource belongs to. Only in the "All" view:
                      with a client picked every row is that client, so the chip
                      would repeat the filter on every line. */}
                  {!clientUID && item.client?.name && (
                    <Chip
                      label={item.client.name}
                      size="small"
                      variant="outlined"
                      color="secondary"
                      title={item.client.name}
                      sx={{ ml: 1, flexShrink: 0, maxWidth: 180 }}
                    />
                  )}
                  {isUserResource(item) && (
                    <Box sx={{ ml: 1, flexShrink: 0, maxWidth: 220 }}>
                      <ConsentBadges consents={item.consents} hasPhone={hasPhone(item)} />
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
                    <IconButton size="small" color="primary" onClick={() => handleView(item)} title="View">
                      <Visibility fontSize="small" />
                    </IconButton>
                    <PermissionWrapper permission={PERMISSIONS.ADD_EDIT_RESOURCE} disableInsteadOfHide={true}>
                      <IconButton size="small" color="info" onClick={() => handleEdit(item)} title="Edit">
                        <Edit fontSize="small" />
                      </IconButton>
                    </PermissionWrapper>
                    <PermissionWrapper permission={PERMISSIONS.ADD_EDIT_RESOURCE} disableInsteadOfHide={true}>
                      <IconButton size="small" color="error" onClick={() => handleDelete(item)} title="Delete">
                        <Delete fontSize="small" />
                      </IconButton>
                    </PermissionWrapper>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
        
        {/* Pagination Controls */}
        {totalPages > 1 && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mt: 3, 
            px: 2, 
            py: 2, 
            borderTop: '1px solid #eee' 
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {Math.min((currentPage - 1) * pageSize + 1, totalItems)} - {Math.min(currentPage * pageSize, totalItems)} of {totalItems} results
              </Typography>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>Per page</InputLabel>
                <Select
                  value={pageSize}
                  label="Per page"
                  onChange={handlePageSizeChange}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Stack spacing={2}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                shape="rounded"
                showFirstButton
                showLastButton
              />
            </Stack>
          </Box>
        )}
        {/* View Dialog */}
        <Dialog 
          open={viewDialog.open} 
          onClose={handleViewClose}
          maxWidth="sm" 
          fullWidth
        >
          <DialogTitle>
            View {(() => {
              if (!viewDialog.data) return '';
              const resourceTypeId = typeof viewDialog.data.resourceTypeId === 'object' ? viewDialog.data.resourceTypeId.id : viewDialog.data.resourceTypeId;
              const resourceType = resourceTypes.find(rt => rt.id === resourceTypeId);
              return typeof viewDialog.data.resourceTypeId === 'object' ? viewDialog.data.resourceTypeId.name : (resourceType?.name || 'Unknown');
            })()} Details
          </DialogTitle>
          <DialogContent>
            {viewDialog.data && (
              <Box>
                {(() => {
                  const resourceTypeId = typeof viewDialog.data.resourceTypeId === 'object' ? viewDialog.data.resourceTypeId.id : viewDialog.data.resourceTypeId;
                  const resourceType = resourceTypes.find(rt => rt.id === resourceTypeId);
                  const fields = typeof viewDialog.data.resourceTypeId === 'object' ? viewDialog.data.resourceTypeId.fields : resourceType?.fields;
                  
                  return fields?.map((field: Field) => {
                    // Get value from nested data object or flattened root level
                    const value = viewDialog.data!.data[field.name] || viewDialog.data![field.name as keyof ResourceData];
                    const isLicense = field.type === 'license_verification' || isLicenseValue(value);
                    // Checked before `isImage`: a licence value nests its images, so
                    // the generic image test would otherwise claim it first.
                    const isImage = !isLicense && (field.type === 'image' || (value && typeof value === 'object' && !Array.isArray(value) && (value.file_path || value.public_url)));

                    return (
                      <Box key={field.name} sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                          {humanizeLabel(field.name)} {field.required && '*'}
                        </Typography>
                        {isLicense ? (
                          isLicenseValue(value) ? (
                            <LicenseVerificationPanel value={value} fieldName={field.name} />
                          ) : (
                            <LicenseVerificationEmpty fieldName={field.name} />
                          )
                        ) : isImage ? (
                          value && typeof value === 'object' && (value.public_url || value.signedUrl) ? (
                            <Box>
                              <Box component="img" src={value.signedUrl || value.public_url} alt={value.file_name || field.name}
                                sx={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}
                                onError={(e: any) => { e.target.src = ''; e.target.alt = 'Image failed to load'; e.target.style.border = '1px dashed #ccc'; e.target.style.padding = '20px'; }} />
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                {value.file_name || 'Image'}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.disabled">No image</Typography>
                          )
                        ) : field.type === 'dropdown' && field.dropdownSource === 'teams' ? (
                          <Typography variant="body1">
                            {value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)
                              ? '-'
                              : resolveTeamNames(value)}
                          </Typography>
                        ) : (
                          <Typography variant="body1">
                            {(() => {
                              try {
                                if (value === undefined || value === null || value === '') return '-';
                                if (typeof value === 'object' && !Array.isArray(value)) return JSON.stringify(value);
                                if (Array.isArray(value)) return value.map(v => typeof v === 'object' ? JSON.stringify(v) : String(v)).join(', ');
                                return String(value);
                              } catch { return '-'; }
                            })()}
                          </Typography>
                        )}
                      </Box>
                    );
                  });
                })()}
                {isUserResource(viewDialog.data) && (
                  <ResourceConsentSection
                    resourceUid={viewDialog.data.id}
                    data={viewDialog.data.data}
                    consents={viewDialog.data.consents}
                    onChanged={loadResourceData}
                  />
                )}
                <Box sx={{ display: 'flex', gap: 4, mt: 3, pt: 2, borderTop: '1px solid #eee' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                      Created At
                    </Typography>
                    <Typography variant="body2">
                      {new Date(viewDialog.data.createdAt).toLocaleString()}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                      Updated At
                    </Typography>
                    <Typography variant="body2">
                      {new Date(viewDialog.data.updatedAt).toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleViewClose}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog 
          open={editDialog.open} 
          onClose={handleEditCancel}
          maxWidth="sm" 
          fullWidth
          PaperProps={{ sx: { borderRadius: 2, maxHeight: '85vh' } }}
        >
          <DialogTitle>
            <Typography variant="h6">
              Edit {(() => {
                if (!editDialog.data) return '';
                const resourceTypeId = typeof editDialog.data.resourceTypeId === 'object' ? editDialog.data.resourceTypeId.id : editDialog.data.resourceTypeId;
                const resourceType = resourceTypes.find(rt => rt.id === resourceTypeId);
                return typeof editDialog.data.resourceTypeId === 'object' ? editDialog.data.resourceTypeId.name : (resourceType?.name || 'Unknown');
              })()}
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            {editFormLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ ml: 2 }}>
                  Loading form data...
                </Typography>
              </Box>
            ) : editDialog.data && (
              <Box key={formRefreshKey} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {(() => {
                  const resourceTypeId = typeof editDialog.data.resourceTypeId === 'object' ? editDialog.data.resourceTypeId.id : editDialog.data.resourceTypeId;
                  const resourceType = resourceTypes.find(rt => rt.id === resourceTypeId);
                  const fields = typeof editDialog.data.resourceTypeId === 'object' ? editDialog.data.resourceTypeId.fields : resourceType?.fields;
                  return fields?.map((field: Field) => (
                    <Box key={field.name}>{renderEditField(field)}</Box>
                  ));
                })()}
                {/* Not part of the form: consent is recorded on its own and never saved
                    through Save Changes. */}
                {isUserResource(editDialog.data) && (
                  <ResourceConsentSection
                    resourceUid={editDialog.data.id}
                    data={editDialog.data.data}
                    consents={editDialog.data.consents}
                    onChanged={loadResourceData}
                  />
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button onClick={handleEditCancel} disabled={editLoading || editFormLoading} variant="outlined">
              Cancel
            </Button>
            <Button 
              onClick={handleEditSave} 
              variant="contained" 
              disabled={editLoading || editFormLoading}
            >
              {editLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Resource Type Changer Dialog */}
        <ResourceTypeChanger
          open={resourceTypeChangerOpen}
          onClose={() => setResourceTypeChangerOpen(false)}
          selectedResources={filteredResults.filter(item => selectedItems.has(item.id))}
          currentResourceTypeId={selectedType !== 'all' ? selectedType : (() => {
            const firstSelected = filteredResults.filter(item => selectedItems.has(item.id))[0];
            if (!firstSelected) return '';
            return typeof firstSelected.resourceTypeId === 'object' ? firstSelected.resourceTypeId.id : firstSelected.resourceTypeId;
          })()}
          clientUID={clientUID}
          onSuccess={handleResourceTypeChangeComplete}
        />
      </Paper>
    </Box>
  );
};

export default UniversalResourceSearch;
